import { useState, useEffect, useCallback, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { useApolloClient } from "@apollo/client/react"
import { GENERATE_SVG_SHAPE, GET_SAVED_SVG_SHAPES } from "@/graphql/queries/admin"
import { SAVE_SVG_SHAPE } from "@/graphql/mutations/admin"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import {
  Circle,
  Triangle,
  Box,
  Clock,
  Copy,
  Check,
  Loader2,
  Shapes,
  Scale,
  Save,
  FolderOpen,
  RefreshCw,
  Trash2,
  TrendingUp,
  BarChart3,
  Thermometer,
  Grid3x3,
  Axis3d,
} from "lucide-react"

// --- Shape parameter definitions ---

interface ParamDef {
  name: string
  type: "number" | "boolean" | "string" | "color"
  default: number | boolean | string
  label: string
  min?: number
  max?: number
}

interface ShapeCategory {
  name: string
  icon: React.ReactNode
  shapes: string[]
}

const CATEGORIES: ShapeCategory[] = [
  {
    name: "2D figūros",
    icon: <Circle className="h-4 w-4" />,
    shapes: ["circle", "square", "rectangle", "triangle", "polygon", "ellipse", "parallelogram", "trapezoid", "rhombus"],
  },
  {
    name: "3D figūros",
    icon: <Box className="h-4 w-4" />,
    shapes: ["cube", "rectangular-prism", "pyramid", "cylinder", "cone", "sphere"],
  },
  {
    name: "Išklotinės",
    icon: <Box className="h-4 w-4" />,
    shapes: ["cube-net", "prism-net"],
  },
  {
    name: "Specialios",
    icon: <Clock className="h-4 w-4" />,
    shapes: ["clock", "angle", "coordinate-grid", "number-line", "fraction-bar", "pie-chart", "bar-chart", "simple-map"],
  },
  {
    name: "Kampai",
    icon: <Triangle className="h-4 w-4" />,
    shapes: ["protractor", "angle-on-protractor", "triangle-with-angles", "parallel-lines", "adjacent-angles", "vertical-angles", "angle-with-arrows"],
  },
  {
    name: "Matavimo įrankiai",
    icon: <Scale className="h-4 w-4" />,
    shapes: ["ruler", "pencil", "cup", "bottle", "pencil-with-ruler", "blocks-row", "balance"],
  },
  {
    name: "Scenos",
    icon: <Shapes className="h-4 w-4" />,
    shapes: ["scene"],
  },
  {
    name: "Funkcijos ir nelygybės",
    icon: <TrendingUp className="h-4 w-4" />,
    shapes: ["function-graph", "parabola", "inequality-number-line", "inequality-plane"],
  },
  {
    name: "Statistika",
    icon: <BarChart3 className="h-4 w-4" />,
    shapes: ["histogram", "scatter-plot", "box-plot", "stem-and-leaf", "venn-diagram", "probability-tree", "frequency-polygon"],
  },
  {
    name: "Matavimas",
    icon: <Thermometer className="h-4 w-4" />,
    shapes: ["thermometer", "graduated-cylinder", "digital-clock", "calendar"],
  },
  {
    name: "Skaičių jausmas",
    icon: <Grid3x3 className="h-4 w-4" />,
    shapes: ["hundreds-grid", "ten-frame", "twenty-frame", "place-value-blocks", "money"],
  },
  {
    name: "Erdvė ir 3D",
    icon: <Axis3d className="h-4 w-4" />,
    shapes: ["isometric-grid", "coordinate-axes-3d", "cross-section"],
  },
]

const COMMON_STYLE_PARAMS: ParamDef[] = [
  { name: "_fill", type: "color", default: "#4a90d9", label: "Fill color" },
  { name: "_stroke", type: "color", default: "#333333", label: "Stroke color" },
]

const SHAPE_PARAMS: Record<string, ParamDef[]> = {
  circle: [
    { name: "radius", type: "number", default: 50, label: "Radius" },
    { name: "show_dimensions", type: "boolean", default: false, label: "Show dimensions" },
    { name: "dimension_value", type: "number", default: 0, label: "Dimension value (0 = use radius)" },
    { name: "unit", type: "string", default: "", label: "Unit (cm, m, mm, etc.)" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  square: [
    { name: "size", type: "number", default: 80, label: "Size" },
    { name: "show_dimensions", type: "boolean", default: false, label: "Show dimensions" },
    { name: "dimension_value", type: "number", default: 0, label: "Dimension value (0 = use size)" },
    { name: "unit", type: "string", default: "", label: "Unit (cm, m, mm, etc.)" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  rectangle: [
    { name: "rect_width", type: "number", default: 120, label: "Width" },
    { name: "rect_height", type: "number", default: 70, label: "Height" },
    { name: "show_dimensions", type: "boolean", default: false, label: "Show dimensions" },
    { name: "dimension_width", type: "number", default: 0, label: "Dimension width (0 = use actual)" },
    { name: "dimension_height", type: "number", default: 0, label: "Dimension height (0 = use actual)" },
    { name: "divisions", type: "number", default: 0, label: "Divisions (0 = none)", min: 0, max: 12 },
    { name: "division_direction", type: "string", default: "", label: "Division direction (vertical/horizontal)" },
    { name: "division_dimension", type: "number", default: 0, label: "One part's side (0 = none)" },
    { name: "division_label", type: "string", default: "", label: "Label inside each part" },
    { name: "unit", type: "string", default: "", label: "Unit (cm, m, mm, etc.)" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  triangle: [
    { name: "type", type: "string", default: "equilateral", label: "Type (equilateral/isosceles/scalene/right)" },
    { name: "base", type: "number", default: 100, label: "Base" },
    { name: "show_angles", type: "boolean", default: false, label: "Show angles" },
    { name: "show_edge_lengths", type: "boolean", default: false, label: "Show edge lengths" },
    { name: "show_vertex_labels", type: "boolean", default: false, label: "Show vertex labels (A,B,C)" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  polygon: [
    { name: "sides", type: "number", default: 5, label: "Sides", min: 3, max: 12 },
    { name: "radius", type: "number", default: 50, label: "Radius" },
    { name: "rotation", type: "number", default: 0, label: "Rotation", min: -180, max: 180 },
    { name: "show_edge_lengths", type: "boolean", default: false, label: "Show edge lengths" },
    { name: "show_vertex_labels", type: "boolean", default: false, label: "Show vertex labels" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  ellipse: [
    { name: "rx", type: "number", default: 60, label: "Radius X" },
    { name: "ry", type: "number", default: 40, label: "Radius Y" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  parallelogram: [
    { name: "base", type: "number", default: 120, label: "Base" },
    { name: "shape_height", type: "number", default: 70, label: "Height" },
    { name: "skew_angle", type: "number", default: 20, label: "Skew angle" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  trapezoid: [
    { name: "top_width", type: "number", default: 60, label: "Top width" },
    { name: "bottom_width", type: "number", default: 120, label: "Bottom width" },
    { name: "shape_height", type: "number", default: 70, label: "Height" },
    { name: "show_dimensions", type: "boolean", default: false, label: "Show dimensions" },
    { name: "dimension_top_width", type: "number", default: 0, label: "Dimension top width (0 = use actual)" },
    { name: "dimension_bottom_width", type: "number", default: 0, label: "Dimension bottom width (0 = use actual)" },
    { name: "dimension_height", type: "number", default: 0, label: "Dimension height (0 = use actual)" },
    { name: "dimension_left_side", type: "number", default: 0, label: "Left leg (0 = not labelled)" },
    { name: "dimension_right_side", type: "number", default: 0, label: "Right leg (0 = not labelled)" },
    { name: "unit", type: "string", default: "", label: "Unit (cm, m, mm, etc.)" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  rhombus: [
    { name: "diagonal_h", type: "number", default: 100, label: "Horizontal diagonal" },
    { name: "diagonal_v", type: "number", default: 70, label: "Vertical diagonal" },
    { name: "show_dimensions", type: "boolean", default: false, label: "Show dimensions" },
    { name: "dimension_diagonal_h", type: "number", default: 0, label: "Dimension diagonal H (0 = use actual)" },
    { name: "dimension_diagonal_v", type: "number", default: 0, label: "Dimension diagonal V (0 = use actual)" },
    { name: "unit", type: "string", default: "", label: "Unit (cm, m, mm, etc.)" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  cube: [
    { name: "size", type: "number", default: 60, label: "Size" },
    { name: "angle", type: "number", default: 30, label: "Perspective angle", min: 15, max: 45 },
    { name: "rotation", type: "number", default: 0, label: "Rotation (-40 to 40)", min: -40, max: 40 },
    { name: "show_hidden_edges", type: "boolean", default: true, label: "Show hidden edges" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  "rectangular-prism": [
    { name: "prism_width", type: "number", default: 80, label: "Width" },
    { name: "prism_height", type: "number", default: 60, label: "Height" },
    { name: "prism_depth", type: "number", default: 40, label: "Depth" },
    { name: "angle", type: "number", default: 30, label: "Perspective angle", min: 15, max: 45 },
    { name: "rotation", type: "number", default: 0, label: "Rotation (-40 to 40)", min: -40, max: 40 },
    { name: "show_hidden_edges", type: "boolean", default: true, label: "Show hidden edges" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  pyramid: [
    { name: "base_size", type: "number", default: 80, label: "Base size" },
    { name: "pyramid_height", type: "number", default: 100, label: "Height" },
    { name: "angle", type: "number", default: 30, label: "Perspective angle", min: 15, max: 45 },
    { name: "show_hidden_edges", type: "boolean", default: true, label: "Show hidden edges" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  cylinder: [
    { name: "radius", type: "number", default: 40, label: "Radius" },
    { name: "cylinder_height", type: "number", default: 80, label: "Height" },
    { name: "angle", type: "number", default: 30, label: "Perspective angle", min: 15, max: 45 },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  cone: [
    { name: "radius", type: "number", default: 40, label: "Radius" },
    { name: "cone_height", type: "number", default: 80, label: "Height" },
    { name: "angle", type: "number", default: 30, label: "Perspective angle", min: 15, max: 45 },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  sphere: [
    { name: "radius", type: "number", default: 50, label: "Radius" },
    { name: "show_equator", type: "boolean", default: true, label: "Show equator" },
    { name: "show_meridian", type: "boolean", default: true, label: "Show meridian" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  "cube-net": [
    { name: "size", type: "number", default: 60, label: "Square size" },
    { name: "layout", type: "string", default: "cross", label: "Layout (cross/plus/t-shape/l-shape/stairs/z-shape — valid; row/square/o-shape — invalid)" },
    { name: "labels", type: "string", default: "", label: "Face labels (comma-separated, e.g. T,A,V,K,D,P)" },
    { name: "label", type: "string", default: "", label: "Overall label" },
  ],
  "prism-net": [
    { name: "length", type: "number", default: 90, label: "Length (l)" },
    { name: "net_width", type: "number", default: 50, label: "Width (w)" },
    { name: "net_height", type: "number", default: 40, label: "Height (h)" },
    { name: "show_dimensions", type: "boolean", default: false, label: "Show dimensions" },
    { name: "unit", type: "string", default: "", label: "Unit (cm, m, ...)" },
    { name: "labels", type: "string", default: "", label: "Face labels (comma-separated: top,left,front,right,back,bottom)" },
    { name: "label", type: "string", default: "", label: "Overall label" },
  ],
  clock: [
    { name: "hour", type: "number", default: 3, label: "Hour (1-12)", min: 1, max: 12 },
    { name: "minute", type: "number", default: 0, label: "Minute (0-59)", min: 0, max: 59 },
    { name: "second", type: "number", default: 0, label: "Second (0-59)", min: 0, max: 59 },
    { name: "radius", type: "number", default: 100, label: "Radius" },
    { name: "show_numbers", type: "boolean", default: true, label: "Show numbers" },
    { name: "show_minute_marks", type: "boolean", default: true, label: "Show minute marks" },
    { name: "show_second_hand", type: "boolean", default: false, label: "Show second hand" },
    { name: "hour_hand_color", type: "color", default: "#333333", label: "Hour hand color" },
    { name: "minute_hand_color", type: "color", default: "#333333", label: "Minute hand color" },
    { name: "second_hand_color", type: "color", default: "#e74c3c", label: "Second hand color" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  angle: [
    { name: "degrees", type: "number", default: 45, label: "Degrees", min: 1, max: 359 },
    { name: "arm_length", type: "number", default: 80, label: "Arm length" },
    { name: "show_arc", type: "boolean", default: true, label: "Show arc" },
    { name: "show_degrees", type: "boolean", default: true, label: "Show degrees" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  "coordinate-grid": [
    { name: "x_min", type: "number", default: -5, label: "X min" },
    { name: "x_max", type: "number", default: 5, label: "X max" },
    { name: "y_min", type: "number", default: -5, label: "Y min" },
    { name: "y_max", type: "number", default: 5, label: "Y max" },
    { name: "grid_size", type: "number", default: 30, label: "Grid size" },
    { name: "show_labels", type: "boolean", default: true, label: "Show labels" },
  ],
  "number-line": [
    { name: "min", type: "number", default: 0, label: "Min" },
    { name: "max", type: "number", default: 10, label: "Max" },
    { name: "step", type: "number", default: 1, label: "Step" },
    { name: "show_arrows", type: "boolean", default: true, label: "Show arrows" },
  ],
  "fraction-bar": [
    { name: "numerator", type: "number", default: 3, label: "Numerator", min: 0 },
    { name: "denominator", type: "number", default: 4, label: "Denominator", min: 1 },
    { name: "show_label", type: "boolean", default: true, label: "Show label" },
    { name: "filled_color", type: "color", default: "#4a90d9", label: "Filled color" },
    { name: "empty_color", type: "color", default: "#e0e0e0", label: "Empty color" },
  ],
  "pie-chart": [
    { name: "radius", type: "number", default: 80, label: "Radius" },
    { name: "show_labels", type: "boolean", default: true, label: "Show labels" },
    { name: "show_legend", type: "boolean", default: true, label: "Show legend" },
  ],
  "bar-chart": [
    { name: "bar_width", type: "number", default: 40, label: "Bar width", min: 10, max: 100 },
    { name: "bar_spacing", type: "number", default: 20, label: "Bar spacing", min: 0, max: 60 },
    { name: "chart_height", type: "number", default: 180, label: "Chart height", min: 80, max: 400 },
    { name: "max_value", type: "number", default: 0, label: "Max value (0 = auto)", min: 0, max: 10000 },
    { name: "grid_lines", type: "number", default: 0, label: "Grid lines (0 = auto)", min: 0, max: 20 },
    { name: "font_size", type: "number", default: 13, label: "Font size", min: 8, max: 20 },
    { name: "show_values", type: "boolean", default: true, label: "Show values" },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "rounded_bars", type: "boolean", default: true, label: "Rounded bars" },
    { name: "title", type: "string", default: "Diagrama", label: "Title" },
    { name: "y_axis_title", type: "string", default: "", label: "Y-axis title" },
    { name: "x_axis_title", type: "string", default: "", label: "X-axis title" },
  ],
  "simple-map": [
    { name: "pixels_per_cm", type: "number", default: 60, label: "Pixels per cm", min: 30, max: 100 },
    { name: "map_width_cm", type: "number", default: 10, label: "Map width (cm)", min: 5, max: 20 },
    { name: "map_height_cm", type: "number", default: 7, label: "Map height (cm)", min: 4, max: 15 },
    { name: "scale_text", type: "string", default: "1 cm = 5 km", label: "Scale text" },
    { name: "scale_bar_cm", type: "number", default: 2, label: "Scale bar length (cm)", min: 1, max: 5 },
    { name: "show_scale_bar", type: "boolean", default: true, label: "Show scale bar" },
    { name: "show_compass", type: "boolean", default: true, label: "Show compass" },
    { name: "show_distances", type: "boolean", default: true, label: "Show distances (cm)" },
    { name: "show_grid", type: "boolean", default: false, label: "Show grid" },
    { name: "show_territory_labels", type: "boolean", default: true, label: "Show territory labels" },
    { name: "map_bg_color", type: "color", default: "#f5f0e1", label: "Background color" },
    { name: "road_color", type: "color", default: "#777777", label: "Road color" },
    { name: "city_color", type: "color", default: "#d94040", label: "City color" },
    { name: "city_radius", type: "number", default: 5, label: "City dot radius", min: 3, max: 10 },
    { name: "font_size", type: "number", default: 12, label: "Font size", min: 8, max: 18 },
  ],
  protractor: [
    { name: "degrees", type: "number", default: 0, label: "Degrees (0=none)", min: 0, max: 180 },
    { name: "radius", type: "number", default: 120, label: "Radius" },
    { name: "show_question", type: "boolean", default: false, label: "Show '?' instead of degrees" },
    { name: "angle_label", type: "string", default: "", label: "Custom angle label (e.g. ?, α, x)" },
    { name: "arm_length", type: "number", default: 20, label: "Arm length" },
    { name: "show_numbers", type: "boolean", default: true, label: "Show numbers" },
    { name: "arm1_color", type: "color", default: "#2196F3", label: "Arm 1 color" },
    { name: "arm2_color", type: "color", default: "#e74c3c", label: "Arm 2 color" },
    { name: "arc_color", type: "color", default: "#4CAF50", label: "Arc color" },
  ],
  "angle-on-protractor": [
    { name: "degrees", type: "number", default: 45, label: "Degrees", min: 1, max: 180 },
    { name: "radius", type: "number", default: 120, label: "Radius" },
    { name: "show_question", type: "boolean", default: false, label: "Show '?' instead of degrees" },
    { name: "angle_label", type: "string", default: "", label: "Custom angle label (e.g. ?, α, x)" },
    { name: "arm_length", type: "number", default: 20, label: "Arm length" },
    { name: "show_numbers", type: "boolean", default: true, label: "Show numbers" },
    { name: "arm1_color", type: "color", default: "#2196F3", label: "Arm 1 color" },
    { name: "arm2_color", type: "color", default: "#e74c3c", label: "Arm 2 color" },
    { name: "arc_color", type: "color", default: "#4CAF50", label: "Arc color" },
  ],
  "triangle-with-angles": [
    { name: "angle_a", type: "number", default: 60, label: "Angle A", min: 1, max: 178 },
    { name: "angle_b", type: "number", default: 70, label: "Angle B", min: 1, max: 178 },
    { name: "base", type: "number", default: 150, label: "Base length" },
    { name: "show_angle_a", type: "boolean", default: true, label: "Show angle A" },
    { name: "show_angle_b", type: "boolean", default: true, label: "Show angle B" },
    { name: "show_angle_c", type: "boolean", default: true, label: "Show angle C" },
    { name: "label_a", type: "string", default: "", label: "Label A (e.g. ?, α)" },
    { name: "label_b", type: "string", default: "", label: "Label B (e.g. ?, β)" },
    { name: "label_c", type: "string", default: "", label: "Label C (e.g. ?, γ)" },
    { name: "show_vertex_labels", type: "boolean", default: true, label: "Show vertex labels" },
    { name: "hide_label", type: "string", default: "", label: "Hide label (A/B/C)" },
    { name: "highlight_angle", type: "string", default: "", label: "Highlight angle (A/B/C)" },
    { name: "arc_radius", type: "number", default: 25, label: "Arc radius" },
    { name: "highlight_color", type: "color", default: "#e74c3c", label: "Highlight color" },
  ],
  "parallel-lines": [
    { name: "transversal_angle", type: "number", default: 60, label: "Transversal angle" },
    { name: "line_length", type: "number", default: 200, label: "Line length" },
    { name: "line_gap", type: "number", default: 80, label: "Gap between lines" },
    { name: "show_angles", type: "boolean", default: false, label: "Show angles" },
    { name: "show_arrows", type: "boolean", default: true, label: "Show arrows" },
    { name: "show_line_lengths", type: "boolean", default: false, label: "Show line lengths" },
  ],
  "adjacent-angles": [
    { name: "angle1", type: "number", default: 40, label: "Angle 1", min: 1, max: 178 },
    { name: "angle2", type: "number", default: 50, label: "Angle 2", min: 1, max: 178 },
    { name: "arm_length", type: "number", default: 100, label: "Arm length" },
    { name: "arc_radius", type: "number", default: 30, label: "Arc radius" },
    { name: "show_sum", type: "boolean", default: false, label: "Show sum" },
    { name: "highlight_angle", type: "number", default: 0, label: "Highlight angle (0=none, 1 or 2)" },
    { name: "highlight_color", type: "color", default: "#e74c3c", label: "Highlight color" },
    { name: "label1", type: "string", default: "", label: "Label 1 (e.g. ?, α)" },
    { name: "label2", type: "string", default: "", label: "Label 2 (e.g. ?, β)" },
  ],
  "vertical-angles": [
    { name: "angle", type: "number", default: 50, label: "Angle", min: 1, max: 179 },
    { name: "arm_length", type: "number", default: 100, label: "Arm length" },
    { name: "arc_radius", type: "number", default: 25, label: "Arc radius" },
    { name: "label1", type: "string", default: "", label: "Label 1" },
    { name: "label2", type: "string", default: "", label: "Label 2" },
    { name: "label3", type: "string", default: "", label: "Label 3" },
    { name: "label4", type: "string", default: "", label: "Label 4" },
  ],
  "angle-with-arrows": [
    { name: "degrees", type: "number", default: 60, label: "Degrees", min: 1, max: 359 },
    { name: "arm_length", type: "number", default: 80, label: "Arm length" },
    { name: "arc_radius", type: "number", default: 30, label: "Arc radius" },
    { name: "show_arc", type: "boolean", default: true, label: "Show arc" },
    { name: "show_degrees", type: "boolean", default: true, label: "Show degrees" },
    { name: "angle_label", type: "string", default: "", label: "Custom angle label (e.g. ?, α, x)" },
    { name: "show_arrows", type: "boolean", default: true, label: "Show arrows" },
    { name: "arrow_style", type: "string", default: "filled", label: "Arrow style (filled/open)" },
    { name: "show_vertex", type: "boolean", default: true, label: "Show vertex dot" },
    { name: "vertex_label", type: "string", default: "", label: "Vertex label" },
    { name: "arm1_label", type: "string", default: "", label: "Arm 1 label" },
    { name: "arm2_label", type: "string", default: "", label: "Arm 2 label" },
    { name: "arm1_color", type: "color", default: "#333333", label: "Arm 1 color" },
    { name: "arm2_color", type: "color", default: "#333333", label: "Arm 2 color" },
    { name: "arc_color", type: "color", default: "#4a90d9", label: "Arc color" },
  ],
  ruler: [
    { name: "length_cm", type: "number", default: 15, label: "Length (cm)", min: 5, max: 30 },
    { name: "orientation", type: "string", default: "horizontal", label: "Orientation (horizontal/vertical)" },
    { name: "show_numbers", type: "boolean", default: true, label: "Show numbers" },
    { name: "show_cm_marks", type: "boolean", default: true, label: "Show cm marks" },
    { name: "show_mm_marks", type: "boolean", default: true, label: "Show mm marks" },
    { name: "pixels_per_cm", type: "number", default: 30, label: "Pixels per cm" },
  ],
  pencil: [
    { name: "length", type: "number", default: 120, label: "Length", min: 60, max: 200 },
    { name: "orientation", type: "string", default: "horizontal", label: "Orientation (horizontal/vertical)" },
    { name: "color", type: "color", default: "#f4c430", label: "Color" },
  ],
  cup: [
    { name: "liquid_percent", type: "number", default: 50, label: "Liquid %", min: 0, max: 100 },
    { name: "cup_height", type: "number", default: 100, label: "Cup height" },
    { name: "cup_width", type: "number", default: 60, label: "Cup width" },
    { name: "liquid_color", type: "color", default: "#87ceeb", label: "Liquid color" },
    { name: "show_measurements", type: "boolean", default: false, label: "Show measurements" },
  ],
  "pencil-with-ruler": [
    { name: "ruler_length_cm", type: "number", default: 15, label: "Ruler length (cm)", min: 10, max: 30 },
    { name: "pencil_length", type: "number", default: 120, label: "Pencil length", min: 60, max: 200 },
  ],
  bottle: [
    { name: "bottle_height", type: "number", default: 120, label: "Bottle height", min: 60, max: 200 },
    { name: "bottle_width", type: "number", default: 50, label: "Bottle width", min: 20, max: 100 },
    { name: "liquid_percent", type: "number", default: 50, label: "Liquid %", min: 0, max: 100 },
    { name: "liquid_color", type: "color", default: "#87ceeb", label: "Liquid color" },
    { name: "bottle_color", type: "color", default: "#e0e8f0", label: "Bottle color" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  "blocks-row": [
    { name: "count", type: "number", default: 5, label: "Block count", min: 1, max: 20 },
    { name: "color", type: "color", default: "#f39c12", label: "Block color" },
    { name: "block_size", type: "number", default: 30, label: "Block size", min: 15, max: 60 },
    { name: "gap", type: "number", default: 2, label: "Gap", min: 0, max: 10 },
    { name: "show_numbers", type: "boolean", default: false, label: "Show numbers" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  balance: [
    { name: "tilt", type: "string", default: "even", label: "Tilt (left/right/even)" },
    { name: "left_label", type: "string", default: "", label: "Left label" },
    { name: "right_label", type: "string", default: "", label: "Right label" },
    { name: "left_color", type: "color", default: "#ff9999", label: "Left pan color" },
    { name: "right_color", type: "color", default: "#99ccff", label: "Right pan color" },
    { name: "label", type: "string", default: "", label: "Label" },
  ],
  scene: [
    { name: "width", type: "number", default: 320, label: "Width", min: 200, max: 500 },
    { name: "height", type: "number", default: 240, label: "Height", min: 150, max: 400 },
    { name: "ground", type: "boolean", default: true, label: "Ground line" },
    { name: "show_viewer", type: "boolean", default: false, label: "Show viewer arrow" },
    { name: "bg_color", type: "color", default: "#f0f4ff", label: "Background color" },
    { name: "border_color", type: "color", default: "#b0c4de", label: "Border color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "function-graph": [
    { name: "x_min", type: "number", default: -5, label: "X min" },
    { name: "x_max", type: "number", default: 5, label: "X max" },
    { name: "y_min", type: "number", default: -5, label: "Y min" },
    { name: "y_max", type: "number", default: 5, label: "Y max" },
    { name: "grid_size", type: "number", default: 30, label: "Grid size" },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "show_labels", type: "boolean", default: true, label: "Show axis labels" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  parabola: [
    { name: "a", type: "number", default: 1, label: "a (coefficient)" },
    { name: "h", type: "number", default: 0, label: "h (vertex x)" },
    { name: "k", type: "number", default: 0, label: "k (vertex y)" },
    { name: "grid_size", type: "number", default: 30, label: "Grid size" },
    { name: "show_vertex", type: "boolean", default: true, label: "Highlight vertex" },
    { name: "show_roots", type: "boolean", default: true, label: "Highlight roots" },
    { name: "show_y_intercept", type: "boolean", default: true, label: "Highlight y-intercept" },
    { name: "label_formula", type: "boolean", default: true, label: "Show formula" },
    { name: "color", type: "color", default: "#2196F3", label: "Curve color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "inequality-number-line": [
    { name: "min", type: "number", default: -10, label: "Min" },
    { name: "max", type: "number", default: 10, label: "Max" },
    { name: "step", type: "number", default: 1, label: "Step" },
    { name: "color", type: "color", default: "#2196F3", label: "Shade color" },
    { name: "show_arrows", type: "boolean", default: true, label: "Show arrows" },
  ],
  "inequality-plane": [
    { name: "x_min", type: "number", default: -5, label: "X min" },
    { name: "x_max", type: "number", default: 5, label: "X max" },
    { name: "y_min", type: "number", default: -5, label: "Y min" },
    { name: "y_max", type: "number", default: 5, label: "Y max" },
    { name: "grid_size", type: "number", default: 30, label: "Grid size" },
    { name: "shade_color", type: "color", default: "#2196F3", label: "Shade color" },
    { name: "line_color", type: "color", default: "#2196F3", label: "Line color" },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "show_labels", type: "boolean", default: true, label: "Show axis labels" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  histogram: [
    { name: "chart_height", type: "number", default: 200, label: "Chart height", min: 80, max: 400 },
    { name: "bar_width", type: "number", default: 40, label: "Bar width", min: 20, max: 100 },
    { name: "show_values", type: "boolean", default: true, label: "Show values" },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "color", type: "color", default: "#4a90d9", label: "Bar color" },
    { name: "title", type: "string", default: "Histograma", label: "Title" },
    { name: "y_axis_title", type: "string", default: "Dažnis", label: "Y-axis title" },
    { name: "x_axis_title", type: "string", default: "", label: "X-axis title" },
  ],
  "scatter-plot": [
    { name: "chart_width", type: "number", default: 300, label: "Chart width", min: 150, max: 500 },
    { name: "chart_height", type: "number", default: 220, label: "Chart height", min: 120, max: 400 },
    { name: "show_trend_line", type: "boolean", default: false, label: "Show trend line" },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "point_color", type: "color", default: "#2196F3", label: "Point color" },
    { name: "trend_color", type: "color", default: "#e74c3c", label: "Trend line color" },
    { name: "point_radius", type: "number", default: 5, label: "Point radius", min: 2, max: 10 },
    { name: "x_label", type: "string", default: "x", label: "X label" },
    { name: "y_label", type: "string", default: "y", label: "Y label" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "box-plot": [
    { name: "orientation", type: "string", default: "horizontal", label: "Orientation (horizontal/vertical)" },
    { name: "color", type: "color", default: "#4a90d9", label: "Box color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "stem-and-leaf": [
    { name: "stem_label", type: "string", default: "Dešimtys", label: "Stem label" },
    { name: "leaf_label", type: "string", default: "Vienetai", label: "Leaf label" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "venn-diagram": [
    { name: "radius", type: "number", default: 80, label: "Circle radius", min: 40, max: 140 },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "probability-tree": [
    { name: "level_spacing", type: "number", default: 150, label: "Level spacing", min: 80, max: 250 },
    { name: "show_outcomes", type: "boolean", default: true, label: "Show outcome labels" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "frequency-polygon": [
    { name: "chart_height", type: "number", default: 200, label: "Chart height", min: 80, max: 400 },
    { name: "bar_width", type: "number", default: 40, label: "Bar/spacing", min: 20, max: 100 },
    { name: "show_points", type: "boolean", default: true, label: "Show points" },
    { name: "fill_area", type: "boolean", default: false, label: "Fill area under line" },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "line_color", type: "color", default: "#e74c3c", label: "Line color" },
    { name: "title", type: "string", default: "Dažnių daugiakampis", label: "Title" },
    { name: "y_axis_title", type: "string", default: "Dažnis", label: "Y-axis title" },
    { name: "x_axis_title", type: "string", default: "", label: "X-axis title" },
  ],
  thermometer: [
    { name: "temperature", type: "number", default: 20, label: "Temperature" },
    { name: "min_temp", type: "number", default: -20, label: "Min temperature" },
    { name: "max_temp", type: "number", default: 40, label: "Max temperature" },
    { name: "step", type: "number", default: 0, label: "Minor tick step (0 = auto)" },
    { name: "label_step", type: "number", default: 0, label: "Number every N° (0 = auto)" },
    { name: "unit", type: "string", default: "°C", label: "Unit" },
    { name: "liquid_color", type: "color", default: "#e74c3c", label: "Liquid color" },
    { name: "show_scale", type: "boolean", default: true, label: "Show scale" },
    { name: "show_readout", type: "boolean", default: false, label: "Show numeric readout (reveals answer)" },
  ],
  "graduated-cylinder": [
    { name: "volume", type: "number", default: 250, label: "Volume" },
    { name: "max_volume", type: "number", default: 500, label: "Max volume" },
    { name: "step", type: "number", default: 50, label: "Scale step" },
    { name: "unit", type: "string", default: "ml", label: "Unit" },
    { name: "liquid_color", type: "color", default: "#87ceeb", label: "Liquid color" },
    { name: "show_minor_ticks", type: "boolean", default: true, label: "Show minor ticks" },
  ],
  "digital-clock": [
    { name: "hour", type: "number", default: 14, label: "Hour (0-23)", min: 0, max: 23 },
    { name: "minute", type: "number", default: 30, label: "Minute (0-59)", min: 0, max: 59 },
    { name: "second", type: "number", default: 0, label: "Second (0-59)", min: 0, max: 59 },
    { name: "show_seconds", type: "boolean", default: false, label: "Show seconds" },
    { name: "format_24h", type: "boolean", default: true, label: "24-hour format" },
    { name: "bg_color", type: "color", default: "#222222", label: "Background color" },
    { name: "digit_color", type: "color", default: "#00ff66", label: "Digit color" },
  ],
  calendar: [
    { name: "month", type: "number", default: 4, label: "Month (1-12)", min: 1, max: 12 },
    { name: "year", type: "number", default: 2026, label: "Year" },
    { name: "week_start", type: "string", default: "monday", label: "Week start (monday/sunday)" },
    { name: "title", type: "string", default: "", label: "Title (auto if empty)" },
  ],
  "hundreds-grid": [
    { name: "start_number", type: "number", default: 1, label: "Start number" },
    { name: "rows", type: "number", default: 10, label: "Rows", min: 1, max: 20 },
    { name: "cols", type: "number", default: 10, label: "Columns", min: 1, max: 20 },
    { name: "cell_size", type: "number", default: 32, label: "Cell size", min: 20, max: 60 },
    { name: "show_numbers", type: "boolean", default: true, label: "Show numbers" },
    { name: "highlight_color", type: "color", default: "#ffeb3b", label: "Highlight color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "ten-frame": [
    { name: "count", type: "number", default: 7, label: "Count", min: 0, max: 10 },
    { name: "frame_size", type: "number", default: 10, label: "Frame size (10 or 20)" },
    { name: "cell_size", type: "number", default: 40, label: "Cell size", min: 25, max: 70 },
    { name: "shape", type: "string", default: "circle", label: "Shape (circle/square/star)" },
    { name: "color", type: "color", default: "#e74c3c", label: "Dot color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "twenty-frame": [
    { name: "count", type: "number", default: 13, label: "Count", min: 0, max: 20 },
    { name: "cell_size", type: "number", default: 40, label: "Cell size", min: 25, max: 70 },
    { name: "shape", type: "string", default: "circle", label: "Shape (circle/square/star)" },
    { name: "color", type: "color", default: "#e74c3c", label: "Dot color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "place-value-blocks": [
    { name: "thousands", type: "number", default: 2, label: "Thousands", min: 0, max: 9 },
    { name: "hundreds", type: "number", default: 3, label: "Hundreds", min: 0, max: 9 },
    { name: "tens", type: "number", default: 4, label: "Tens", min: 0, max: 20 },
    { name: "ones", type: "number", default: 5, label: "Ones", min: 0, max: 20 },
    { name: "unit_size", type: "number", default: 10, label: "Unit size", min: 5, max: 20 },
    { name: "show_totals", type: "boolean", default: true, label: "Show totals" },
  ],
  money: [
    { name: "show_total", type: "boolean", default: true, label: "Show total" },
    { name: "currency_symbol", type: "string", default: "€", label: "Currency symbol" },
  ],
  "isometric-grid": [
    { name: "rows", type: "number", default: 10, label: "Rows", min: 3, max: 20 },
    { name: "cols", type: "number", default: 10, label: "Columns", min: 3, max: 20 },
    { name: "spacing", type: "number", default: 30, label: "Spacing", min: 15, max: 60 },
    { name: "style", type: "string", default: "dots", label: "Style (dots/lines)" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "coordinate-axes-3d": [
    { name: "x_max", type: "number", default: 5, label: "X max", min: 2, max: 12 },
    { name: "y_max", type: "number", default: 5, label: "Y max", min: 2, max: 12 },
    { name: "z_max", type: "number", default: 5, label: "Z max", min: 2, max: 12 },
    { name: "unit_size", type: "number", default: 30, label: "Unit size", min: 15, max: 60 },
    { name: "show_grid", type: "boolean", default: true, label: "Show grid" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
  "cross-section": [
    { name: "solid", type: "string", default: "cube", label: "Solid (cube/cylinder/cone/sphere/pyramid)" },
    { name: "plane", type: "string", default: "horizontal", label: "Plane (horizontal/vertical/diagonal)" },
    { name: "size", type: "number", default: 90, label: "Size", min: 40, max: 150 },
    { name: "highlight_color", type: "color", default: "#e74c3c", label: "Highlight color" },
    { name: "title", type: "string", default: "", label: "Title" },
  ],
}

const SHAPE_DISPLAY_NAMES: Record<string, string> = {
  circle: "Circle",
  square: "Square",
  rectangle: "Rectangle",
  triangle: "Triangle",
  polygon: "Polygon",
  ellipse: "Ellipse",
  parallelogram: "Parallelogram",
  trapezoid: "Trapezoid",
  rhombus: "Rhombus",
  cube: "Cube",
  "rectangular-prism": "Rectangular Prism",
  pyramid: "Pyramid",
  cylinder: "Cylinder",
  cone: "Cone",
  sphere: "Sphere",
  "cube-net": "Cube Net (išklotinė)",
  "prism-net": "Prism Net (išklotinė)",
  clock: "Clock",
  angle: "Angle",
  "coordinate-grid": "Coordinate Grid",
  "number-line": "Number Line",
  "fraction-bar": "Fraction Bar",
  "pie-chart": "Pie Chart",
  "bar-chart": "Bar Chart",
  "simple-map": "Simple Map",
  protractor: "Protractor",
  "angle-on-protractor": "Angle on Protractor",
  "triangle-with-angles": "Triangle with Angles",
  "parallel-lines": "Parallel Lines",
  "adjacent-angles": "Adjacent Angles",
  "vertical-angles": "Vertical Angles",
  "angle-with-arrows": "Angle with Arrows",
  ruler: "Ruler",
  pencil: "Pencil",
  cup: "Cup",
  bottle: "Bottle",
  "pencil-with-ruler": "Pencil with Ruler",
  "blocks-row": "Blocks Row",
  balance: "Balance Scale",
  scene: "Scene (Spatial)",
  "function-graph": "Function Graph",
  parabola: "Parabola",
  "inequality-number-line": "Inequality on Number Line",
  "inequality-plane": "Inequality Half-Plane",
  histogram: "Histogram",
  "scatter-plot": "Scatter Plot",
  "box-plot": "Box-and-Whisker Plot",
  "stem-and-leaf": "Stem-and-Leaf Plot",
  "venn-diagram": "Venn Diagram",
  "probability-tree": "Probability Tree",
  "frequency-polygon": "Frequency Polygon",
  thermometer: "Thermometer",
  "graduated-cylinder": "Graduated Cylinder",
  "digital-clock": "Digital Clock",
  calendar: "Calendar",
  "hundreds-grid": "Hundreds Grid",
  "ten-frame": "Ten Frame",
  "twenty-frame": "Twenty Frame",
  "place-value-blocks": "Place-Value Blocks",
  money: "Money (Euros)",
  "isometric-grid": "Isometric Grid",
  "coordinate-axes-3d": "3D Coordinate Axes",
  "cross-section": "Cross-Section",
}

// Default values for pie-chart and bar-chart (complex params sent as JSON)
function getDefaultParams(shapeType: string): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  const defs = SHAPE_PARAMS[shapeType] || []
  for (const def of defs) {
    params[def.name] = def.default
  }
  for (const def of COMMON_STYLE_PARAMS) {
    params[def.name] = def.default
  }

  // Add default complex params for charts
  if (shapeType === "pie-chart") {
    params.values = [
      { value: 30, label: "A", color: "#4a90d9" },
      { value: 20, label: "B", color: "#e74c3c" },
      { value: 50, label: "C", color: "#2ecc71" },
    ]
  }
  if (shapeType === "bar-chart") {
    params.values = [
      { value: 5, label: "Obuoliai", color: "#4a90d9" },
      { value: 8, label: "Kriaušės", color: "#e74c3c" },
      { value: 3, label: "Bananai", color: "#2ecc71" },
    ]
  }
  if (shapeType === "scene") {
    params.objects = [
      { type: "teddy", position: "left", label: "" },
      { type: "ball", position: "right", label: "" },
    ]
  }
  if (shapeType === "function-graph") {
    params.functions = [
      { type: "quadratic", a: 1, b: 0, c: -4, color: "#2196F3", label: "y = x² - 4" },
    ]
  }
  if (shapeType === "histogram") {
    params.bins = [
      { from: 0, to: 10, value: 3 },
      { from: 10, to: 20, value: 7 },
      { from: 20, to: 30, value: 12 },
      { from: 30, to: 40, value: 8 },
      { from: 40, to: 50, value: 4 },
    ]
  }
  if (shapeType === "scatter-plot") {
    params.points = [
      { x: 1, y: 2 }, { x: 2, y: 4 }, { x: 3, y: 5 },
      { x: 4, y: 8 }, { x: 5, y: 9 }, { x: 6, y: 11 },
    ]
  }
  if (shapeType === "box-plot") {
    params.data = [2, 3, 5, 6, 7, 8, 9, 10, 12, 15]
  }
  if (shapeType === "stem-and-leaf") {
    params.data = [12, 15, 18, 23, 25, 27, 31, 34, 34, 36, 42, 45, 48, 51, 55]
  }
  if (shapeType === "venn-diagram") {
    params.sets = [
      { name: "A", color: "#e74c3c" },
      { name: "B", color: "#2196F3" },
    ]
    params.regions = { A: "8", B: "5", AB: "3" }
  }
  if (shapeType === "probability-tree") {
    params.root = {
      label: "",
      children: [
        { label: "H", probability: "1/2", children: [
          { label: "H", probability: "1/2" },
          { label: "T", probability: "1/2" },
        ]},
        { label: "T", probability: "1/2", children: [
          { label: "H", probability: "1/2" },
          { label: "T", probability: "1/2" },
        ]},
      ],
    }
  }
  if (shapeType === "frequency-polygon") {
    params.bins = [
      { from: 0, to: 10, value: 3 },
      { from: 10, to: 20, value: 7 },
      { from: 20, to: 30, value: 12 },
      { from: 30, to: 40, value: 8 },
      { from: 40, to: 50, value: 4 },
    ]
  }
  if (shapeType === "inequality-number-line") {
    params.inequalities = [{ op: ">=", value: 2 }]
  }
  if (shapeType === "inequality-plane") {
    params.inequality = { slope: 1, intercept: 0, op: ">" }
  }
  if (shapeType === "money") {
    params.composition = [
      { denomination: 2, count: 2 },
      { denomination: 1, count: 3 },
      { denomination: 0.5, count: 2 },
      { denomination: 5, count: 1 },
    ]
  }
  if (shapeType === "hundreds-grid") {
    params.highlight = ["multiples:5"]
  }
  if (shapeType === "calendar") {
    params.highlight_days = [1, 15, 22]
  }
  if (shapeType === "coordinate-axes-3d") {
    params.points = [
      { x: 2, y: 1, z: 3, label: "P", color: "#9c27b0" },
    ]
  }
  if (shapeType === "simple-map") {
    params.cities = [
      { name: "Vilnius", x: 7.5, y: 4 },
      { name: "Kaunas", x: 5, y: 3.5 },
      { name: "Klaipėda", x: 1.5, y: 2.5 },
      { name: "Šiauliai", x: 3.5, y: 1.2 },
      { name: "Panevėžys", x: 6, y: 1.5 },
    ]
    params.roads = [
      { from: 0, to: 1 },
      { from: 1, to: 2 },
      { from: 1, to: 3 },
      { from: 3, to: 4 },
      { from: 4, to: 0 },
    ]
    params.territories = [
      {
        name: "Miškas",
        color: "#3a7d32",
        opacity: 0.2,
        points: [
          { x: 0.3, y: 0.3 }, { x: 2.5, y: 0.2 }, { x: 3.2, y: 0.8 },
          { x: 3, y: 2 }, { x: 1.8, y: 2.3 }, { x: 0.4, y: 1.6 },
        ],
      },
      {
        name: "Ežeras",
        color: "#2980b9",
        opacity: 0.3,
        points: [
          { x: 5.5, y: 2.5 }, { x: 6.8, y: 2.3 }, { x: 7.2, y: 2.8 },
          { x: 6.8, y: 3.3 }, { x: 5.8, y: 3.2 }, { x: 5.2, y: 2.9 },
        ],
      },
      {
        name: "Laukai",
        color: "#d4a843",
        opacity: 0.18,
        points: [
          { x: 2.5, y: 4 }, { x: 4.5, y: 4.2 }, { x: 5, y: 5.5 },
          { x: 4, y: 6.5 }, { x: 2, y: 6.2 }, { x: 1.5, y: 5 },
        ],
      },
      {
        name: "Parkas",
        color: "#27ae60",
        opacity: 0.22,
        points: [
          { x: 7, y: 4.8 }, { x: 8.5, y: 4.5 }, { x: 9.2, y: 5.2 },
          { x: 9, y: 6.2 }, { x: 7.5, y: 6.5 }, { x: 6.8, y: 5.5 },
        ],
      },
    ]
  }

  return params
}

// --- Components ---

function ShapeListItem({
  shapeType,
  isSelected,
  onSelect,
  thumbnail,
}: {
  shapeType: string
  isSelected: boolean
  onSelect: () => void
  thumbnail: string | null
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border-2 p-3 transition-colors hover:border-primary/50 hover:bg-accent/50 ${
        isSelected ? "border-primary bg-accent" : "border-transparent"
      }`}
    >
      <div className="flex items-center gap-3">
        {thumbnail ? (
          <div
            className="h-10 w-10 flex-shrink-0 flex items-center justify-center [&>svg]:max-h-full [&>svg]:max-w-full"
            dangerouslySetInnerHTML={{ __html: thumbnail }}
          />
        ) : (
          <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded bg-muted">
            <Shapes className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
        <span className="text-sm font-medium truncate">
          {SHAPE_DISPLAY_NAMES[shapeType] || shapeType}
        </span>
      </div>
    </button>
  )
}

function ParameterForm({
  shapeType,
  values,
  onChange,
}: {
  shapeType: string
  values: Record<string, unknown>
  onChange: (name: string, value: unknown) => void
}) {
  const shapeParams = SHAPE_PARAMS[shapeType] || []
  const allParams = [...shapeParams, ...COMMON_STYLE_PARAMS]

  if (allParams.length === 0) {
    return <p className="text-sm text-muted-foreground">No configurable parameters.</p>
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {shapeParams.map((param) => (
          <div key={param.name} className="space-y-1">
            {param.type === "boolean" ? (
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  id={param.name}
                  checked={values[param.name] as boolean}
                  onCheckedChange={(checked) => onChange(param.name, !!checked)}
                />
                <Label htmlFor={param.name} className="text-sm cursor-pointer">
                  {param.label}
                </Label>
              </div>
            ) : param.type === "number" ? (
              <>
                <Label htmlFor={param.name} className="text-sm">
                  {param.label}
                </Label>
                <Input
                  id={param.name}
                  type="number"
                  value={values[param.name] as number}
                  min={param.min}
                  max={param.max}
                  onChange={(e) => onChange(param.name, Number(e.target.value))}
                  className="h-8"
                />
              </>
            ) : param.type === "color" ? (
              <>
                <Label htmlFor={param.name} className="text-sm">
                  {param.label}
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id={`${param.name}_picker`}
                    value={(values[param.name] as string) || String(param.default ?? "")}
                    onChange={(e) => onChange(param.name, e.target.value)}
                    className="h-8 w-12 rounded border cursor-pointer"
                  />
                  <Input
                    id={param.name}
                    type="text"
                    value={(values[param.name] as string) || ""}
                    onChange={(e) => onChange(param.name, e.target.value)}
                    className="h-8 flex-1 font-mono text-xs"
                    placeholder="#000000"
                  />
                </div>
              </>
            ) : (
              <>
                <Label htmlFor={param.name} className="text-sm">
                  {param.label}
                </Label>
                <Input
                  id={param.name}
                  type="text"
                  value={(values[param.name] as string) || ""}
                  onChange={(e) => onChange(param.name, e.target.value)}
                  className="h-8"
                />
              </>
            )}
          </div>
        ))}
      </div>
      <div className="border-t pt-3">
        <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">Style</p>
        <div className="flex gap-4">
          {COMMON_STYLE_PARAMS.map((param) => (
            <div key={param.name} className="space-y-1">
              <Label htmlFor={param.name} className="text-sm">
                {param.label}
              </Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id={`${param.name}_picker`}
                  value={(values[param.name] as string) || String(param.default ?? "")}
                  onChange={(e) => onChange(param.name, e.target.value)}
                  className="h-8 w-8 rounded border cursor-pointer"
                />
                <Input
                  id={param.name}
                  type="text"
                  value={(values[param.name] as string) || ""}
                  onChange={(e) => onChange(param.name, e.target.value)}
                  className="h-8 w-28 font-mono text-xs"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const SCENE_OBJECT_TYPES = [
  "tree", "house", "table", "ball", "teddy", "cat", "dog", "car",
  "flower", "star", "book", "box", "sun", "cloud", "bird", "apple",
]

const SCENE_POSITIONS = [
  "center", "left", "right", "top", "bottom",
  "top-left", "top-right", "bottom-left", "bottom-right",
  "behind", "in-front",
]

function SceneObjectsEditor({
  objects,
  onChange,
}: {
  objects: Array<{ type: string; position: string; color?: string; label?: string }>
  onChange: (objects: Array<{ type: string; position: string; color?: string; label?: string }>) => void
}) {
  const updateObject = (index: number, field: string, value: string) => {
    const next = objects.map((obj, i) => (i === index ? { ...obj, [field]: value } : obj))
    onChange(next)
  }

  const addObject = () => {
    onChange([...objects, { type: "ball", position: "center" }])
  }

  const removeObject = (index: number) => {
    onChange(objects.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Objects</p>
        <Button variant="outline" size="sm" onClick={addObject}>+ Add object</Button>
      </div>
      {objects.map((obj, i) => (
        <div key={i} className="flex items-end gap-2 rounded border p-2 bg-muted/30">
          <div className="space-y-1 flex-1">
            <Label className="text-xs">Type</Label>
            <select
              value={obj.type}
              onChange={(e) => updateObject(i, "type", e.target.value)}
              className="w-full h-8 rounded border bg-background px-2 text-sm"
            >
              {SCENE_OBJECT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1 flex-1">
            <Label className="text-xs">Position</Label>
            <select
              value={obj.position}
              onChange={(e) => updateObject(i, "position", e.target.value)}
              className="w-full h-8 rounded border bg-background px-2 text-sm"
            >
              {SCENE_POSITIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1 w-20">
            <Label className="text-xs">Color</Label>
            <input
              type="color"
              value={obj.color || "#4a90d9"}
              onChange={(e) => updateObject(i, "color", e.target.value)}
              className="h-8 w-full rounded border cursor-pointer"
            />
          </div>
          <div className="space-y-1 flex-1">
            <Label className="text-xs">Label</Label>
            <Input
              type="text"
              value={obj.label || ""}
              onChange={(e) => updateObject(i, "label", e.target.value)}
              className="h-8"
              placeholder="Optional"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removeObject(i)}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          >
            &times;
          </Button>
        </div>
      ))}
      {objects.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-2">No objects. Click &quot;+ Add object&quot; to start.</p>
      )}
    </div>
  )
}

const ALL_SHAPES = CATEGORIES.flatMap((c) => c.shapes)

function parseParamsFromSearchParams(searchParams: URLSearchParams, shapeType: string): Record<string, unknown> {
  const defaults = getDefaultParams(shapeType)
  const defs = [...(SHAPE_PARAMS[shapeType] || []), ...COMMON_STYLE_PARAMS]

  for (const def of defs) {
    const urlValue = searchParams.get(def.name)
    if (urlValue === null) continue
    if (def.type === "boolean") {
      defaults[def.name] = urlValue === "true" || urlValue === "1"
    } else if (def.type === "number") {
      const n = Number(urlValue)
      if (!isNaN(n)) defaults[def.name] = n
    } else {
      defaults[def.name] = urlValue
    }
  }
  return defaults
}

function buildSearchParams(shapeType: string, params: Record<string, unknown>): URLSearchParams {
  const defaults = getDefaultParams(shapeType)
  const sp = new URLSearchParams()
  sp.set("shape", shapeType)

  const defs = [...(SHAPE_PARAMS[shapeType] || []), ...COMMON_STYLE_PARAMS]
  for (const def of defs) {
    const val = params[def.name]
    const defVal = defaults[def.name]
    if (val !== defVal && val !== "" && val !== undefined) {
      sp.set(def.name, String(val))
    }
  }
  return sp
}

export default function SvgShapesPage() {
  const client = useApolloClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const initialShape = ALL_SHAPES.includes(searchParams.get("shape") || "") ? searchParams.get("shape")! : "circle"
  const [selectedShape, setSelectedShape] = useState<string>(initialShape)
  const [paramValues, setParamValues] = useState<Record<string, unknown>>(() =>
    parseParamsFromSearchParams(searchParams, initialShape)
  )
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [svgHtml, setSvgHtml] = useState("")
  const [loading, setLoading] = useState(false)
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({})
  const fetchIdRef = useRef(0)

  const buildParamsJson = useCallback((params: Record<string, unknown>) => {
    // Parameters where 0 means "auto/default" and should be omitted
    const zeroMeansAuto = new Set([
      "max_value", "grid_lines", "step", "label_step", "divisions", "division_dimension",
    ])

    const cleanParams: Record<string, unknown> = {}
    const style: Record<string, string> = {}
    for (const [key, value] of Object.entries(params)) {
      if (value === "" || value === undefined) continue
      if (zeroMeansAuto.has(key) && value === 0) continue
      if (key === "_fill") {
        style.fill = value as string
      } else if (key === "_stroke") {
        style.stroke = value as string
      } else if (key === "labels" && typeof value === "string") {
        const parts = value.split(",").map((s) => s.trim()).filter((s) => s.length > 0)
        if (parts.length > 0) cleanParams[key] = parts
      } else {
        cleanParams[key] = value
      }
    }
    if (Object.keys(style).length > 0) {
      cleanParams.style = style
    }
    return JSON.stringify(cleanParams)
  }, [])

  // Load thumbnails sequentially in background
  useEffect(() => {
    let cancelled = false
    async function loadThumbnails() {
      for (const shape of ALL_SHAPES) {
        if (cancelled) break
        try {
          const result = await client.query({
            query: GENERATE_SVG_SHAPE,
            variables: {
              shapeType: shape,
              paramsJson: buildParamsJson(getDefaultParams(shape)),
            },
            fetchPolicy: "cache-first",
          })
          if (!cancelled && result.data?.generateSvgShape?.svg) {
            setThumbnails((prev) => ({
              ...prev,
              [shape]: result.data.generateSvgShape.svg,
            }))
          }
        } catch {
          // skip failed thumbnails
        }
      }
    }
    loadThumbnails()
    return () => { cancelled = true }
  }, [client, buildParamsJson])

  const generateShape = useCallback(async (shape: string, params: Record<string, unknown>) => {
    const id = ++fetchIdRef.current
    setError(null)
    setLoading(true)

    try {
      const result = await client.query({
        query: GENERATE_SVG_SHAPE,
        variables: {
          shapeType: shape,
          paramsJson: buildParamsJson(params),
        },
        fetchPolicy: "no-cache",
      })
      if (id === fetchIdRef.current) {
        setSvgHtml(result.data?.generateSvgShape?.svg || "")
        setLoading(false)
      }
    } catch (err: unknown) {
      if (id === fetchIdRef.current) {
        setError(err instanceof Error ? err.message : "Failed to generate shape")
        setLoading(false)
      }
    }
  }, [client, buildParamsJson])

  // Generate on initial mount
  useEffect(() => {
    generateShape(selectedShape, paramValues)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateUrl = useCallback((shape: string, params: Record<string, unknown>) => {
    setSearchParams(buildSearchParams(shape, params), { replace: true })
  }, [setSearchParams])

  const handleSelectShape = (shapeType: string) => {
    const newParams = getDefaultParams(shapeType)
    setSelectedShape(shapeType)
    setParamValues(newParams)
    setError(null)
    setSavedPath(null)
    setSaveError(null)
    updateUrl(shapeType, newParams)
    generateShape(shapeType, newParams)
  }

  const handleParamChange = (name: string, value: unknown) => {
    setParamValues((prev) => {
      const next = { ...prev, [name]: value }
      updateUrl(selectedShape, next)
      return next
    })
  }

  // Save state
  const [saving, setSaving] = useState(false)
  const [savedPath, setSavedPath] = useState<string | null>(null)
  const [customPath, setCustomPath] = useState("")
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedShapes, setSavedShapes] = useState<Array<{
    svgPath: string; yamlPath: string; shapeType: string; paramsJson: string; svg: string
  }>>([])
  const [loadingSaved, setLoadingSaved] = useState(false)

  const handleSave = async (outputPath?: string) => {
    setSaving(true)
    setSaveError(null)
    try {
      const variables: Record<string, string> = {
        shapeType: selectedShape,
        paramsJson: buildParamsJson(paramValues),
      }
      if (outputPath) variables.outputPath = outputPath
      const result = await client.mutate({
        mutation: SAVE_SVG_SHAPE,
        variables,
      })
      const data = result.data?.saveSvgShape
      if (data?.success) {
        setSavedPath(data.svgPath)
        setSvgHtml(data.svg || svgHtml)
        loadSavedShapes()
      } else {
        setSaveError(data?.error || "Save failed")
      }
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const loadSavedShapes = useCallback(async () => {
    setLoadingSaved(true)
    try {
      const result = await client.query({
        query: GET_SAVED_SVG_SHAPES,
        fetchPolicy: "no-cache",
      })
      setSavedShapes(result.data?.savedSvgShapes || [])
    } catch {
      // ignore
    } finally {
      setLoadingSaved(false)
    }
  }, [client])

  // Load saved shapes on mount
  useEffect(() => {
    loadSavedShapes()
  }, [loadSavedShapes])

  const handleLoadSavedShape = (shape: { shapeType: string; paramsJson: string; svgPath: string; svg: string }) => {
    setSelectedShape(shape.shapeType)
    const savedParams = JSON.parse(shape.paramsJson)
    // Convert style back to _fill/_stroke
    const newParams: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(savedParams)) {
      if (key === "style" && typeof value === "object" && value !== null) {
        const style = value as Record<string, string>
        if (style.fill) newParams._fill = style.fill
        if (style.stroke) newParams._stroke = style.stroke
      } else {
        newParams[key] = value
      }
    }
    // Fill in defaults for params not in saved data
    const defaults = getDefaultParams(shape.shapeType)
    for (const [key, val] of Object.entries(defaults)) {
      if (!(key in newParams)) newParams[key] = val
    }
    setParamValues(newParams)
    updateUrl(shape.shapeType, newParams)
    setSvgHtml(shape.svg)
    setSavedPath(shape.svgPath)
    setSaveError(null)
  }

  const handleCopySvg = async () => {
    if (svgHtml) {
      await navigator.clipboard.writeText(svgHtml)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">SVG figūros</h1>
        <p className="text-muted-foreground">
          Interactive SVG shape generator playground
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar: shape list (sticky) */}
        <div className="w-72 flex-shrink-0 self-start sticky top-4">
          <Card className="overflow-hidden" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shapes className="h-4 w-4" />
                Shapes
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-track]:bg-transparent" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
              <div className="px-3 pb-3 space-y-4">
                {CATEGORIES.map((category) => (
                  <div key={category.name}>
                    <div className="flex items-center gap-2 mb-2 px-1">
                      {category.icon}
                      <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                        {category.name}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {category.shapes.map((shape) => (
                        <ShapeListItem
                          key={shape}
                          shapeType={shape}
                          isSelected={selectedShape === shape}
                          onSelect={() => handleSelectShape(shape)}
                          thumbnail={thumbnails[shape] || null}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: playground */}
        <div className="flex-1 space-y-4">
          {/* SVG Preview */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {SHAPE_DISPLAY_NAMES[selectedShape] || selectedShape}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{selectedShape}</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopySvg}
                    disabled={!svgHtml}
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-1" />
                        Copy SVG
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center min-h-[300px] rounded-lg border bg-white p-6">
                {loading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                ) : error ? (
                  <div className="text-destructive text-sm text-center">
                    <p className="font-medium">Error generating shape</p>
                    <p className="mt-1 text-xs">{error}</p>
                  </div>
                ) : (
                  <div
                    className="[&>svg]:max-w-full [&>svg]:max-h-[400px]"
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Parameters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Parameters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ParameterForm
                shapeType={selectedShape}
                values={paramValues}
                onChange={handleParamChange}
              />
              {selectedShape === "scene" && (
                <SceneObjectsEditor
                  objects={(paramValues.objects as Array<{ type: string; position: string; color?: string; label?: string }>) || []}
                  onChange={(objects) => {
                    setParamValues((prev) => ({ ...prev, objects }))
                  }}
                />
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={() => generateShape(selectedShape, paramValues)} disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Generate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const defaults = getDefaultParams(selectedShape)
                    setParamValues(defaults)
                    updateUrl(selectedShape, defaults)
                  }}
                >
                  Reset defaults
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save to file */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Save className="h-4 w-4" />
                Save
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleSave()}
                  disabled={saving || !svgHtml}
                  size="sm"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-3 w-3 mr-1" />
                  {savedPath ? "Re-save" : "Save"}
                </Button>
                <Button
                  onClick={() => handleSave(customPath || undefined)}
                  disabled={saving || !svgHtml || !customPath}
                  size="sm"
                  variant="outline"
                >
                  Save to path
                </Button>
                <Input
                  type="text"
                  placeholder="content/exercises/.../shape.svg"
                  value={customPath}
                  onChange={(e) => setCustomPath(e.target.value)}
                  className="h-8 font-mono text-xs flex-1"
                />
              </div>
              {savedPath && (
                <div className="rounded bg-muted px-3 py-2 font-mono text-xs text-muted-foreground">
                  {savedPath}
                </div>
              )}
              {saveError && (
                <p className="text-xs text-destructive">{saveError}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Saved shapes gallery */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Saved shapes ({savedShapes.length})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadSavedShapes} disabled={loadingSaved}>
              {loadingSaved ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {savedShapes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No saved shapes yet. Generate and save a shape above.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {savedShapes.map((shape) => (
                <button
                  key={shape.svgPath}
                  onClick={() => handleLoadSavedShape(shape)}
                  className="rounded-lg border p-3 hover:border-primary/50 hover:bg-accent/50 transition-colors text-left"
                >
                  <div
                    className="h-16 flex items-center justify-center mb-2 [&>svg]:max-h-full [&>svg]:max-w-full"
                    dangerouslySetInnerHTML={{ __html: shape.svg }}
                  />
                  <p className="text-xs font-medium truncate">
                    {SHAPE_DISPLAY_NAMES[shape.shapeType] || shape.shapeType}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate font-mono">
                    {shape.svgPath.split("/").pop()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
